import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'reflexbox';
import styled from 'styled-components';

import { ChevronRightIcon } from '@treams/treams-icons';
import { Accordion } from '@treams/treams-ui/common/Accordion';
import { Avatar } from '@treams/treams-ui/common/Avatar';
import { Button } from '@treams/treams-ui/common/button/Button';
import { Card } from '@treams/treams-ui/common/Card';
import { FlexGap } from '@treams/treams-ui/common/FlexGap';
import { SegmentedControl } from '@treams/treams-ui/common/SegmentedControl';
import { Heading3, Label, Paragraph } from '@treams/treams-ui/common/Text';
import {
	ArrowDownIcon,
	ArrowUpIcon,
	CashIcon,
	DoubleChevronDownIcon,
	DoubleChevronUpIcon,
} from '@treams/treams-ui/icons';
import { tokens } from '@treams/treams-ui/theme/tokens';

import { formatSalary } from '../../salary-development/formatSalary';
import {
	GENDER_GAP_GENDERS,
	type GenderGap,
	type GenderGapGender,
	type GenderGapRow,
} from './buildGenderGap';
import { type RewardSort, averageSalary } from './buildRewardFunctions';
import { ColumnHeader } from './RewardFunctions';
import { matchRewardRow, personMatches } from './rewardSearch';

const GENDER_COLORS: Record<GenderGapGender, string> = {
	MALE: tokens.info.icon,
	FEMALE: tokens.brand.icon,
	OTHER: tokens.warning.icon,
};

const Track = styled.div`
	flex: 1;
	min-width: 0;
	height: 8px;
	border-radius: ${tokens.radius.full}px;
	background: #efeef9;
	overflow: hidden;
`;

const Fill = styled.div<{ $share: number; $gender: GenderGapGender }>`
	height: 100%;
	width: ${(props) => props.$share * 100}%;
	border-radius: ${tokens.radius.full}px;
	background: ${(props) => GENDER_COLORS[props.$gender]};
`;

const Dot = styled.span<{ $gender: GenderGapGender }>`
	display: inline-block;
	flex-shrink: 0;
	width: 8px;
	height: 8px;
	border-radius: ${tokens.radius.full}px;
	background: ${(props) => GENDER_COLORS[props.$gender]};
`;

const Amount = styled(Box)`
	flex: 0 0 196px;
	white-space: nowrap;
`;

const GapRow = styled(FlexGap)<{ $hover?: boolean }>`
	${(props) =>
		props.$hover &&
		`
		&:hover {
			background: ${tokens.surface.hover};
		}
	`}
`;

function rowMax(row: GenderGapRow) {
	return Math.max(
		0,
		...row.bars.map((bar) => bar.average ?? 0),
		...row.employees.map((employee) => employee.salary),
	);
}

function femaleDifference(row: GenderGapRow): number | undefined {
	const male = row.bars.find((bar) => bar.gender === 'MALE')?.average;
	const female = row.bars.find((bar) => bar.gender === 'FEMALE')?.average;

	if (male == null || female == null || male === 0) {
		return undefined;
	}

	return Math.round(((female - male) / male) * 100);
}

function Bar({
	label,
	gender,
	amount,
	max,
	text,
	salaryIcon,
}: {
	label: string;
	gender: GenderGapGender;
	amount?: number;
	max: number;
	text: string;
	salaryIcon?: boolean;
}) {
	return (
		<FlexGap alignItems="center" gap={tokens.gap.element} py="2px">
			<Track
				role="meter"
				aria-label={label}
				aria-valuemin={0}
				aria-valuemax={Math.round(max)}
				aria-valuenow={amount == null ? undefined : Math.round(amount)}
			>
				{amount != null && max > 0 && <Fill $gender={gender} $share={amount / max} />}
			</Track>
			<Amount>
				<FlexGap alignItems="center" gap={tokens.gap.inline}>
					{salaryIcon && text !== '–' && (
						<Box aria-hidden style={{ display: 'flex', color: tokens.icon.tertiary }}>
							<CashIcon size={14} />
						</Box>
					)}
					<Paragraph small>{text}</Paragraph>
				</FlexGap>
			</Amount>
		</FlexGap>
	);
}

function Chevron({ expanded }: { expanded?: boolean }) {
	return (
		<Box
			style={{
				display: 'flex',
				flexShrink: 0,
				width: 16,
				color: tokens.icon.tertiary,
				transform: expanded ? 'rotate(90deg)' : undefined,
				transition: 'transform 120ms ease-out',
			}}
		>
			{expanded != null && <ChevronRightIcon size={16} />}
		</Box>
	);
}

function Row({
	row,
	max,
	isTotal,
	expanded,
}: {
	row: GenderGapRow;
	max: number;
	isTotal?: boolean;
	expanded?: boolean;
}) {
	const { i18n, t } = useTranslation();
	const difference = femaleDifference(row);
	const Name = isTotal ? Heading3 : expanded != null ? Label : Paragraph;

	return (
		<GapRow
			$hover={expanded != null}
			px={3}
			alignItems="center"
			gap={tokens.gap.list}
			py={`${tokens.padding.control}px`}
			style={isTotal ? { borderTop: `1px solid ${tokens.border.subtle}` } : undefined}
		>
			<Chevron expanded={expanded} />
			<Box flex="1 1 160px" minWidth={0} maxWidth={280}>
				<Name mb={difference != null ? 2 : 0}>{row.name}</Name>
				{difference != null && (
					<Paragraph small>
						{t('analytics.reward.genderGap.difference', {
							percent: `${difference > 0 ? '+' : ''}${difference}`,
						})}
					</Paragraph>
				)}
			</Box>
			<Box flex="2 1 220px" minWidth={0}>
				{row.bars
					.filter((bar) => bar.average != null)
					.map((bar) => (
						<Bar
							key={bar.gender}
							label={`${row.name}: ${t(`settings.employees.personal.gender.${bar.gender}`)}`}
							gender={bar.gender}
							amount={bar.average}
							max={max}
							text={`${t('analytics.reward.genderGap.average')} ${formatSalary(
								Math.round(bar.average!),
								i18n.language,
							)} (${t('analytics.reward.genderGap.people', { count: bar.count })})`}
						/>
					))}
			</Box>
		</GapRow>
	);
}

function Employees({
	row,
	max,
	query,
	personMatch,
}: {
	row: GenderGapRow;
	max: number;
	query: string;
	personMatch: boolean;
}) {
	const { i18n, t } = useTranslation();

	return (
		<Box px={3} style={{ paddingBottom: tokens.padding.container }}>
			{row.employees.map((employee) => (
				<FlexGap
					key={employee.id}
					alignItems="center"
					gap={tokens.gap.list}
					py={`${tokens.gap.inline}px`}
					style={{ opacity: personMatch && !personMatches(employee, query) ? 0.4 : undefined }}
				>
					<Chevron />
					<FlexGap
						flex="1 1 160px"
						minWidth={0}
						maxWidth={280}
						alignItems="center"
						gap={tokens.gap.element}
						style={{ paddingLeft: tokens.gap.section }}
					>
						<Avatar
							size={32}
							flexShrink={0}
							user={{ firstName: employee.firstName, lastName: employee.lastName }}
						/>
						<Paragraph>{employee.name}</Paragraph>
					</FlexGap>
					<Box flex="2 1 220px" minWidth={0}>
						<Bar
							label={`${employee.name}: ${t(`settings.employees.personal.gender.${employee.gender}`)}`}
							gender={employee.gender}
							amount={employee.salary}
							max={max}
							salaryIcon
							text={formatSalary(Math.round(employee.salary), i18n.language)}
						/>
					</Box>
				</FlexGap>
			))}
		</Box>
	);
}

export function GenderGapTable({
	genderGap,
	query = '',
}: {
	genderGap: GenderGap;
	query?: string;
}) {
	const { i18n, t } = useTranslation();
	const [open, setOpen] = useState<Record<string, boolean>>({});
	const [openQuery, setOpenQuery] = useState(query);

	// A new search decides again which rows open; earlier toggles would otherwise win.
	if (openQuery !== query) {
		setOpenQuery(query);
		setOpen({});
	}
	const [sort, setSort] = useState<RewardSort>('name');
	const functions = useMemo(() => {
		const rows = [...genderGap.functions];

		rows.sort((left, right) => {
			if (left.id === 'none') return 1;
			if (right.id === 'none') return -1;

			if (sort === 'deviation') {
				const leftGap = femaleDifference(left);
				const rightGap = femaleDifference(right);
				const leftScore = leftGap == null ? -1 : Math.abs(leftGap);
				const rightScore = rightGap == null ? -1 : Math.abs(rightGap);

				if (leftScore !== rightScore) {
					return rightScore - leftScore;
				}
			}

			if (sort === 'salary-desc' || sort === 'salary-asc') {
				const leftSalary = averageSalary(left.employees.map((employee) => employee.salary));
				const rightSalary = averageSalary(right.employees.map((employee) => employee.salary));

				if (leftSalary == null && rightSalary != null) return 1;
				if (leftSalary != null && rightSalary == null) return -1;

				if (leftSalary != null && rightSalary != null && leftSalary !== rightSalary) {
					return sort === 'salary-desc' ? rightSalary - leftSalary : leftSalary - rightSalary;
				}
			}

			return left.name.localeCompare(right.name, i18n.language);
		});

		return rows;
	}, [genderGap.functions, i18n.language, sort]);
	const visible = useMemo(
		() =>
			functions.flatMap((row) => {
				if (!query.trim()) {
					return [{ row, personMatch: false }];
				}

				const match = matchRewardRow(row.name, row.employees, query);

				return match ? [{ row, personMatch: match === 'person' }] : [];
			}),
		[functions, query],
	);
	const expandedFor = (id: string, personMatch: boolean) => open[id] ?? personMatch;
	const allOpen =
		visible.length > 0 && visible.every((item) => expandedFor(item.row.id, item.personMatch));

	return (
		<Card>
			<FlexGap alignItems="center" justifyContent="space-between" gap={tokens.gap.list}>
				<Heading3>{t('analytics.reward.genderGap.title')}</Heading3>
				<FlexGap alignItems="center" gap={tokens.gap.list}>
					<SegmentedControl
						value={sort}
						options={[
							{ title: t('analytics.reward.sort.name'), value: 'name' },
							{ title: t('analytics.reward.sort.deviation'), value: 'deviation' },
							{
								title: t('analytics.reward.sort.salaryDesc'),
								trailingIcon: <ArrowDownIcon />,
								value: 'salary-desc',
							},
							{
								title: t('analytics.reward.sort.salaryAsc'),
								trailingIcon: <ArrowUpIcon />,
								value: 'salary-asc',
							},
						]}
						onChange={(value) => setSort(value as RewardSort)}
					/>
					<Button
						small
						type="secondary"
						icon={allOpen ? <DoubleChevronUpIcon /> : <DoubleChevronDownIcon />}
						title={t(allOpen ? 'analytics.reward.collapseAll' : 'analytics.reward.expandAll')}
						aria-label={t(allOpen ? 'analytics.reward.collapseAll' : 'analytics.reward.expandAll')}
						onClick={() =>
							setOpen(Object.fromEntries(visible.map((item) => [item.row.id, !allOpen])))
						}
					/>
				</FlexGap>
			</FlexGap>
			<FlexGap mt={3} gap={tokens.gap.group} flexWrap="wrap">
				{GENDER_GAP_GENDERS.map((gender) => (
					<FlexGap key={gender} alignItems="center" gap={tokens.gap.element}>
						<Dot $gender={gender} />
						<Paragraph small>{t(`settings.employees.personal.gender.${gender}`)}</Paragraph>
					</FlexGap>
				))}
			</FlexGap>
			<ColumnHeader
				labels={[
					t('analytics.reward.genderGap.column.function'),
					t('analytics.reward.genderGap.column.salary'),
				]}
			/>
			<Box mx={-3} mb={-3}>
				{visible.length === 0 && (
					<Paragraph small px={3} py={3} style={{ color: tokens.text.tertiary }}>
						{t('analytics.reward.noResults')}
					</Paragraph>
				)}
				{visible.map(({ row, personMatch }) => {
					const expanded = expandedFor(row.id, personMatch);
					const max = rowMax(row);

					return (
						<Accordion
							key={row.id}
							borderless
							noPadding
							isExpanded={expanded}
							onClick={() => setOpen((current) => ({ ...current, [row.id]: !expanded }))}
							customHeader={<Row row={row} max={max} expanded={expanded} />}
						>
							<Employees row={row} max={max} query={query} personMatch={personMatch} />
						</Accordion>
					);
				})}
				{!query.trim() && <Row row={genderGap.total} max={rowMax(genderGap.total)} isTotal />}
			</Box>
		</Card>
	);
}
